<script setup>
import { onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { ApiError, useApi } from 'soykajs/composables/api'
import { useMessageStack } from '@/stores/messagestack'
import { useUserStore } from '@/stores/user'
import PasswordInput from '@/components/password.vue'
import BlockScreen from '@/components/blockscreen.vue'
import ChangePass from './changePass.vue'
import TotpChallenge from '@/components/TotpChallenge.vue'
import { useRoute, useRouter } from 'vue-router'

const { t } = useI18n()

const model = reactive({ username: null, password: null, access_token: null, service: 'ltms' })
const { user } = storeToRefs(useUserStore())
const { push: openModal } = useMessageStack()
const loading = ref(false)
const showChangePass = ref(false)
const showBlockedModal = ref(false)
const show2faInfo = ref(false)
const showTotp = ref(false)
const emailFor2fa = ref('')
const totpEnabled = ref(false)
const route = useRoute()
const router = useRouter()

const usernameEl = ref(null)
const passwordEl = ref(null)

function syncCredentialsFromDom() {
  const u = usernameEl.value?.value
  const p = passwordEl.value?.getRawValue?.()
  if (typeof u === 'string') model.username = u
  if (typeof p === 'string') model.password = p
}

function isBlockedLoginError(raw) {
  const s = String(raw ?? '').toLowerCase()
  return s.includes('forbidden') || s.includes('not active') || s.includes('inactive') || s.includes('неактив')
}

function closeBlockedModal() {
  showBlockedModal.value = false
}

function open2faInfo(email, enabled) {
  emailFor2fa.value = email || ''
  totpEnabled.value = !!enabled
  show2faInfo.value = true
  showTotp.value = false
}

function openTotpFrom2fa() {
  showTotp.value = true
}

function close2faInfo() {
  show2faInfo.value = false
  showTotp.value = false
}

function onTotpSuccess(payload) {
  show2faInfo.value = false
  showTotp.value = false
  if (payload?.access_token) {
    user.value = { ...user.value, access_token: payload.access_token }
  }
}

onMounted(async () => {
  // При повторном заходе на страницу логина в SPA не тянем старую учётку.
  model.username = null
  model.password = null

  const loginError = route.query.login_error
  if (!loginError) return
  if (isBlockedLoginError(loginError)) {
    showBlockedModal.value = true
  } else {
    await openModal({ type: 'error', message: String(loginError) })
  }
  const nextQuery = { ...route.query }
  delete nextQuery.login_error
  await router.replace({ name: 'login', query: nextQuery })
})

const { refresh: doLogin } = useApi({
  model,
  method: 'POST',
  url: 'login',
  transform: d => {
    delete d.access_token
    return d
  },
})

async function submit() {
  try {
    syncCredentialsFromDom()
    loading.value = true
    showChangePass.value = false
    showBlockedModal.value = false
    show2faInfo.value = false
    showTotp.value = false
    delete model.require_2fa
    delete model.access_token
    delete model.totp_enabled
    await doLogin()
    if (model.require_2fa) {
      loading.value = false
      open2faInfo(model.email_notifications || model.email, model.totp_enabled)
      return
    }
    user.value = { ...user.value, access_token: model.access_token }
  } catch (err) {
    loading.value = false
    if (err instanceof ApiError && err.status === 422) {
      showChangePass.value = true
    } else if (err instanceof ApiError && err.status === 403) {
      showBlockedModal.value = true
    } else if (err instanceof Error && /\(422\)/.test(String(err))) {
      showChangePass.value = true
    } else if (err instanceof Error && String(err).includes('403')) {
      openModal({ type: 'error', message: err.message || String(err) })
    } else if (err instanceof ApiError && err.code === 401) {
      openModal({ type: 'error', message: t('loginErrorCredentials') })
    } else {
      openModal({ type: 'error', message: t('loginErrorGeneric') })
    }
  }
}

function onChangePassSuccess(payload) {
  showChangePass.value = false
  if (payload?.require_2fa) {
    open2faInfo(payload.email || model.email_notifications || model.email, payload.totp_enabled)
    return
  }
  openModal({ type: 'info', message: t('passwordChanged') })
}

function onChangePassClose() {
  showChangePass.value = false
}
</script>
<template>
  <Teleport
    to="body"
    v-if="loading"
  >
    <BlockScreen />
  </Teleport>
  <Teleport to="body">
    <div
      v-if="showBlockedModal"
      class="modal fade show login-page-modal"
      style="display: block"
    >
      <div class="modal-dialog modal-dialog-centered login-modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ t('blockedTitle') }}</h5>
            <button
              type="button"
              class="btn-close"
              :aria-label="t('common.close')"
              @click="closeBlockedModal"
            />
          </div>
          <div class="modal-body">
            {{ t('blockedBody') }}
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="showBlockedModal"
      class="modal-backdrop fade show"
    ></div>
  </Teleport>
  <Teleport to="body">
    <div
      v-if="show2faInfo"
      class="modal fade show login-page-modal"
      style="display: block"
    >
      <div class="modal-dialog modal-dialog-centered login-modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ showTotp ? t('totpTitle') : t('twoFaTitle') }}</h5>
            <button
              type="button"
              class="btn-close"
              :aria-label="t('common.close')"
              @click="close2faInfo"
            />
          </div>
          <div class="modal-body">
            <template v-if="!showTotp">
              <p class="mb-3">
                {{
                  emailFor2fa
                    ? t('twoFaLinkSentWithEmail', { email: emailFor2fa })
                    : t('twoFaLinkSent')
                }}
              </p>
              <div
                v-if="totpEnabled"
                class="text-center"
              >
                <button
                  type="button"
                  class="btn btn-outline-primary"
                  @click="openTotpFrom2fa"
                >
                  {{ t('enterCode') }}
                </button>
                <div class="form-text mt-1">{{ t('letterNotReceived') }}</div>
              </div>
              <p
                v-else
                class="small text-muted mb-0"
              >
                {{ t('totpBindInProfile') }}
              </p>
            </template>
            <TotpChallenge
              v-else
              :username="model.username"
              :password="model.password"
              :service="model.service"
              @success="onTotpSuccess"
              @close="showTotp = false"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="show2faInfo"
      class="modal-backdrop fade show"
    ></div>
  </Teleport>
  <Teleport to="body">
  <div
    v-if="showChangePass"
    class="modal fade show login-page-modal"
    style="display: block"
  >
    <div class="modal-dialog modal-dialog-centered login-modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ t('changePassTitle') }}</h5>
          <button
            type="button"
            class="btn-close"
            :aria-label="t('common.close')"
            @click="onChangePassClose"
          />
        </div>
        <div class="modal-body">
          <ChangePass
            :username="model.username"
            :service="model.service"
            @success="onChangePassSuccess"
            @close="onChangePassClose"
          />
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="showChangePass"
    class="modal-backdrop fade show"
  ></div>
  </Teleport>
  <form @submit.prevent="submit">
    <div class="mb-3">
      <label class="form-label">{{ t('common.login') }}</label>
      <input
        type="text"
        class="form-control"
        name="username"
        ref="usernameEl"
        v-model="model.username"
      />
    </div>
    <div class="mb-3">
      <label class="form-label">{{ t('common.password') }}</label>
      <PasswordInput
        name="password"
        ref="passwordEl"
        v-model="model.password"
        autocomplete="current-password"
      />
    </div>
    <button
      type="submit"
      class="btn btn-primary"
    >
      {{ t('signIn') }}
    </button>
  </form>
</template>

<style scoped>
.login-modal-dialog {
  max-width: 500px;
  width: 95%;
  margin-right: auto;
  margin-left: auto;
}
</style>

<i18n>
{
  "ru": {
    "loginErrorCredentials": "Неправильный логин или пароль",
    "loginErrorGeneric": "Произошла ошибка при попытке входа",
    "twoFaLinkSentWithEmail": "Ссылка для входа отправлена на почту {email}. Перейдите по ссылке из письма, чтобы войти в систему.",
    "twoFaLinkSent": "Ссылка для входа отправлена на вашу почту. Перейдите по ссылке из письма, чтобы войти в систему.",
    "passwordChanged": "Пароль успешно изменён. Войдите с новым паролем.",
    "blockedTitle": "Доступ заблокирован",
    "blockedBody": "Доступ заблокирован. Обратитесь к администратору",
    "changePassTitle": "Смена пароля",
    "signIn": "Войти",
    "twoFaTitle": "Подтверждение входа",
    "totpTitle": "Вход по коду",
    "enterCode": "Ввести код",
    "letterNotReceived": "письмо не пришло",
    "totpBindInProfile": "Привязка приложения-аутентификатора — в личном кабинете после входа по ссылке из письма."
  },
  "en": {
    "loginErrorCredentials": "Invalid login or password",
    "loginErrorGeneric": "Login failed",
    "twoFaLinkSentWithEmail": "A sign-in link was sent to {email}. Open the email and follow the link to sign in.",
    "twoFaLinkSent": "A sign-in link was sent to your email. Open the email and follow the link to sign in.",
    "passwordChanged": "Password changed successfully. Please sign in with the new password.",
    "blockedTitle": "Access blocked",
    "blockedBody": "Access is blocked. Please contact an administrator.",
    "changePassTitle": "Change password",
    "signIn": "Sign in",
    "twoFaTitle": "Confirm sign-in",
    "totpTitle": "Sign in with code",
    "enterCode": "Enter code",
    "letterNotReceived": "email not received",
    "totpBindInProfile": "Link an authenticator app in your profile after signing in via the email link."
  }
}
</i18n>
